import { useEffect, useState, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import { Dialog, DialogContent } from "../Dialog/Dialog"
import { Input } from "../Input/Input"

export type UndeployDialogMode = "version" | "group"

export interface UndeployDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode?: UndeployDialogMode
  title: ReactNode
  description: ReactNode
  confirmValue: string
  placeholder?: string
  confirmLabel?: string
  cancelLabel?: string
  inputMono?: boolean
  onConfirm?: () => void
  className?: string
}

export function UndeployDialog({
  open,
  onOpenChange,
  mode = "version",
  title,
  description,
  confirmValue,
  placeholder,
  confirmLabel,
  cancelLabel = "Cancel",
  inputMono,
  onConfirm,
  className,
}: UndeployDialogProps) {
  const [typed, setTyped] = useState("")

  useEffect(() => {
    if (!open) setTyped("")
  }, [open])

  const canConfirm = typed === confirmValue
  const defaultConfirmLabel = mode === "group" ? "Delete all" : "Delete"
  const effectiveConfirmLabel = confirmLabel ?? defaultConfirmLabel
  const effectiveInputMono = inputMono ?? mode === "version"

  const handleCancel = () => onOpenChange(false)
  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm?.()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel()
        else onOpenChange(true)
      }}
    >
      <DialogContent className={cn("v1 sm:max-w-sm p-5 gap-0 overflow-hidden", className)}>
        <h3 className="text-section text-foreground mb-1">{title}</h3>
        <p className="text-caption text-muted-foreground leading-relaxed mb-3">
          {description}
        </p>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canConfirm) handleConfirm()
            if (e.key === "Escape") handleCancel()
          }}
          autoFocus
          placeholder={placeholder ?? confirmValue}
          className={cn("mb-4 h-8 text-body", effectiveInputMono && "font-mono")}
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-caption"
            onClick={handleCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            className="text-caption disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {effectiveConfirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
